import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Banner,
  Card,
  Delta,
  Findings,
  Kpi,
  Kpis,
  MetricCell,
  RowLabel,
  TableCard,
  fmt,
  type Col,
} from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport } from "../useReport";
import { Modal } from "./Modal";
import {
  Spark,
  SkuTrendModal,
  TrendModal,
  type TrendSpec,
  type TrendWindow,
} from "./Trend";
import {
  TREND_METRICS,
  type CeoAtoms,
  type GeoRow,
  type SkuRow,
  type SublineRow,
  type TrendMetric,
  basisLabel,
  findings,
  geoPool,
  osaSeriesOf,
  periodLabel,
  ppTone,
  reconcile,
  seriesTone,
  hasSublines,
  skuPool,
  span,
  sublinePool,
  toneOf,
  useCeoAtoms,
} from "./data";
import {
  bandColor,
  marketPool,
  national,
  ownDailySeries,
  type Atom,
  type Basis,
  type BrandRow,
} from "./pool";
import type { DateRange } from "@/lib/dateRange";

/* ── shared props ─────────────────────────────────────────────────────────
 * Basis lives in CeoView, not here: it is page chrome (it sits in the filter
 * row beside the category chip) and all three sub-pages must agree on it —
 * "every rupee on this page carries a basis" is only true if it is one basis.
 */
export interface PageProps {
  customer: string;
  category?: string | undefined;
  range: DateRange;
  basis: Basis;
}

/** The MRP / SP segmented pair. Always visible, never hidden behind a menu. */
export function BasisToggle({
  basis,
  onChange,
}: {
  basis: Basis;
  onChange: (b: Basis) => void;
}) {
  return (
    <div
      className="rds"
      title="Plan and target are set on MRP. SP is what was realised after discount."
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".06em",
          color: "var(--ink-3)",
          textTransform: "uppercase",
        }}
      >
        Basis
      </span>
      <div className="seg">
        {(["mrp", "sp"] as const).map((b) => (
          <button
            key={b}
            className={basis === b ? "on" : ""}
            onClick={() => {
              onChange(b);
            }}
          >
            {basisLabel(b)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── small renderers ──────────────────────────────────────────────────────
 * A percentage-POINT delta.
 *
 * Not ds's <Delta pp>, whose flat band is 0.05 where the report's ppHTML() uses
 * 0.02. With ds's band a 0.03 pp share move renders grey while the sparkline
 * beside it — coloured by ppTone, which does use 0.02 — renders green, so the
 * number and the line would disagree about the same move. Raised for ds; this
 * keeps both halves of the colour rule on the report's threshold meanwhile.
 */
function Pp({
  cur,
  prev,
}: {
  cur: number | null | undefined;
  prev: number | null | undefined;
}) {
  const c = typeof cur === "number" && Number.isFinite(cur) ? cur : null;
  const p = typeof prev === "number" && Number.isFinite(prev) ? prev : null;
  if (c === null || p === null)
    return <div className="delta flat">no comparison</div>;
  const d = Math.round((c - p) * 100) / 100;
  const tone = Math.abs(d) < 0.02 ? "flat" : d > 0 ? "up" : "down";
  return (
    <div className={`delta ${tone}`}>
      {d > 0 ? "+" : ""}
      {d.toFixed(2)} pp
    </div>
  );
}

/** A value coloured by its own threshold band: <70 red, <85 amber, else green. */
function Banded({
  v,
  kind,
  bold,
}: {
  v: number | null;
  kind: "pct" | "pct2";
  bold?: boolean;
}) {
  return (
    <span
      style={{
        color: bandColor(v) ?? "var(--ink-3)",
        ...(bold ? { fontWeight: 600 } : {}),
      }}
    >
      {fmt(v, kind)}
    </span>
  );
}

/**
 * A rupee CHANGE, not a percentage — the portfolio tables' `Change` column.
 * U+2212 for the minus sign, as the report has, because a hyphen at 13.5px
 * beside a ₹ reads as a dash.
 */
function RupeeChange({ chg }: { chg: number | null }) {
  if (chg === null)
    return <span className="stat-val muted">no comparison</span>;
  const colour =
    chg > 0 ? "var(--up)" : chg < 0 ? "var(--down)" : "var(--ink-3)";
  return (
    // nowrap: the sign is its own text node, so a narrow column broke
    // "+₹22,890" across two lines with the plus stranded above the number.
    <span style={{ color: colour, fontWeight: 600, whiteSpace: "nowrap" }}>
      {chg > 0 ? "+" : chg < 0 ? "−" : ""}
      {fmt(Math.abs(chg), "money")}
    </span>
  );
}

/**
 * Renders the `<b>…</b>` the findings engine emits.
 *
 * A split, not dangerouslySetInnerHTML: the strings interpolate brand and SKU
 * names that come from the warehouse, and there is no reason for a rival's
 * brand name to be able to inject markup into this page.
 */
function richParts(
  text: string,
): Array<{ key: string; bold: boolean; text: string }> {
  const out: Array<{ key: string; bold: boolean; text: string }> = [];
  let bold = false;
  text.split(/(<b>|<\/b>)/).forEach((p, i) => {
    if (p === "<b>") {
      bold = true;
      return;
    }
    if (p === "</b>") {
      bold = false;
      return;
    }
    if (p === "") return;
    // Position is the identity: this is one immutable string split once.
    out.push({ key: `${String(i)}:${p.slice(0, 12)}`, bold, text: p });
  });
  return out;
}

function Rich({ text }: { text: string }) {
  return (
    <>
      {richParts(text).map((p) =>
        p.bold ? (
          <b key={p.key}>{p.text}</b>
        ) : (
          <span key={p.key}>{p.text}</span>
        ),
      )}
    </>
  );
}

/* ── the drill stack ──────────────────────────────────────────────────────
 * The report pushes the previous modal's markup onto a stack, so a SKU trend
 * opened from inside a sub-line drill returns to that drill rather than to the
 * page. Modelled here as a stack of descriptors instead of a stack of HTML.
 */
type Drill =
  | { kind: "trend"; spec: TrendSpec }
  | { kind: "geo"; name: string }
  | { kind: "brand"; name: string }
  | { kind: "subline"; name: string }
  | { kind: "sku"; pid: string };

function useDrill(): {
  top: Drill | null;
  push: (d: Drill) => void;
  pop: () => void;
  openTrend: (spec: TrendSpec) => void;
} {
  const [stack, setStack] = useState<Drill[]>([]);
  const push = useCallback((d: Drill) => {
    setStack((s) => [...s, d]);
  }, []);
  const pop = useCallback(() => {
    setStack((s) => s.slice(0, -1));
  }, []);
  const openTrend = useCallback(
    (spec: TrendSpec) => {
      push({ kind: "trend", spec });
    },
    [push],
  );
  return { top: stack[stack.length - 1] ?? null, push, pop, openTrend };
}

/* ── page chrome ─────────────────────────────────────────────────────────── */

/**
 * The reconciliation banner. Three outcomes, exactly as the report has them:
 * a red hard stop on drift, a grey note when a check could not be run, and
 * NOTHING at all on a clean pass — a green tick on every load teaches the
 * reader to stop looking at the strip that matters.
 */
function ReconBanner({ data, basis }: { data: CeoAtoms; basis: Basis }) {
  const r = useMemo(
    () =>
      reconcile(
        (b) =>
          national(
            data.geo_atoms,
            data.own,
            data.window.start,
            data.window.end,
            b,
          ),
        (b) => geoPool(data, data.window.start, data.window.end, b),
        (b) => skuPool(data, data.window.start, data.window.end, b),
      ),
    [data],
  );

  if (r.errors.length) {
    return (
      <div
        className="banner"
        style={{
          background: "#fde8e8",
          border: "2px solid var(--down)",
          color: "var(--down)",
          fontWeight: 600,
        }}
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6.5" />
          <path d="M8 5v3.5M8 11h.01" strokeLinecap="round" />
        </svg>
        <div>
          RECONCILIATION FAILED — this page&apos;s own figures do not add up:{" "}
          {r.errors.join(" · ")}. Do not use these figures until this is
          resolved.
        </div>
      </div>
    );
  }
  return (
    <Banner>
      <b>Contract check not run.</b> The MRP geography rows sum to the national
      headline exactly, so the page is internally consistent on the basis where
      that is an identity. The published report also reconciles six headline
      figures against the MCP&apos;s own stored contract; that contract is not
      served by this endpoint, so the check is reported as not run rather than
      as a pass. Current basis: {basisLabel(basis)}.
      {r.notes.map((n) => (
        <span key={n.slice(0, 24)}>
          <br />
          {n}
        </span>
      ))}
    </Banner>
  );
}

/** The basis strip. Always rendered, on all three sub-pages. */
function BasisBanner({
  data,
  basis,
  prevDays,
  curDays,
}: {
  data: CeoAtoms;
  basis: Basis;
  prevDays: number;
  curDays: number;
}) {
  const w = data.window;
  const partial =
    w.comparison_available !== false && prevDays > 0 && prevDays < curDays;
  const none = w.comparison_available === false || prevDays === 0;
  return (
    <Banner>
      <b>One basis, stated everywhere.</b> {data.own}&apos;s rupees are{" "}
      <b>measured</b> — taken straight from the brand&apos;s own portal download
      (all listings, combo volume included), so MRP GMV matches the partner
      portal to the rupee. SP is those portal units at the crawl selling price,
      because the portal publishes no realised price.{" "}
      <b>OSA is not on this basis</b>: it stays crawl-derived over single
      listings. Rivals have no portal, so their rupees are{" "}
      <b>crawl estimates</b>. Every rupee on this page is on the{" "}
      <b>{basisLabel(basis)}</b> basis (plan and targets are set on MRP; SP is
      what was realised); ASP is a selling price and ignores the toggle. Figures
      that cannot follow the date picker at all are tagged <i>fixed window</i>.
      Data ends {w.as_of} — the earliest day every feed has settled.
      {none || partial ? (
        <>
          <br />
          <b>
            {none ? "No comparison available:" : "Comparison is partial:"}
          </b>{" "}
          {none
            ? `none of the days in ${periodLabel(w.previous_start, w.previous_end)} exist`
            : `${prevDays} of ${curDays} days exist for ${periodLabel(w.previous_start, w.previous_end)}`}{" "}
          — no delta is shown rather than one computed over days that were never
          crawled.
        </>
      ) : null}
      {w.note ? (
        <>
          <br />
          {w.note}
        </>
      ) : null}
    </Banner>
  );
}

/* ── loading / error / empty ─────────────────────────────────────────────── */

function Gate({
  customer,
  loading,
  error,
  data,
}: {
  customer: string;
  loading: boolean;
  error: Error | null;
  data: CeoAtoms | null;
}): ReactNode {
  if (customer === "") return <State>Pick a brand to see the CEO view.</State>;
  if (error) return <ErrorBox>{error.message}</ErrorBox>;
  if (loading || !data) return <State>Loading…</State>;
  return null;
}

/** How many days the own brand's atoms actually cover in a response. */
const daysOf = (d: CeoAtoms | null): number =>
  d ? (d.national_atoms[d.own] ?? []).length : 0;

/* ══ 1. Market position ═══════════════════════════════════════════════════ */

export function MarketPositionPage({
  customer,
  category,
  range,
  basis,
}: PageProps) {
  const { loading, error, cur, prev, win } = useCeoAtoms(
    customer,
    range,
    category,
  );
  const mkt = useReport("/v1/reports/marketing/overview", customer, range);
  const drill = useDrill();
  const gate = Gate({ customer, loading, error, data: cur });
  if (gate) return gate;
  if (!cur || !win) return null;

  const [f, t] = span(cur);
  const basisL = basisLabel(basis);
  const rawNow = marketPool(cur, f, t, basis);
  const rawPrv = prev
    ? marketPool(prev, prev.window.start, prev.window.end, basis)
    : null;

  // 14zkq6bd1nm — When the API returns universe_market_share, override share
  // values AND market size on ALL pools so every number on the page matches the
  // MCP's city-by-city calibration. One number everywhere.
  const uniShare = cur.universe_market_share;
  const uniSize = cur.universe_market_size;
  const now = uniShare
    ? {
        ...rawNow,
        brands: rawNow.brands.map((b) => ({
          ...b,
          share: uniShare[b.name] ?? b.share,
        })),
        other: rawNow.other
          ? {
              ...rawNow.other,
              share: uniShare[rawNow.other.name] ?? rawNow.other.share,
            }
          : null,
        size: uniSize ?? rawNow.size,
      }
    : rawNow;
  const prevUniShare = prev?.universe_market_share;
  const prv = rawPrv
    ? prevUniShare
      ? {
          ...rawPrv,
          brands: rawPrv.brands.map((b) => ({
            ...b,
            share: prevUniShare[b.name] ?? b.share,
          })),
          other: rawPrv.other
            ? {
                ...rawPrv.other,
                share: prevUniShare[rawPrv.other.name] ?? rawPrv.other.share,
              }
            : null,
          size: prev.universe_market_size ?? rawPrv.size,
        }
      : rawPrv
    : null;

  const me = now.brands.find((b) => b.name === cur.own) ?? null;
  const mePrev = prv?.brands.find((b) => b.name === cur.own) ?? null;
  const ser = ownDailySeries(cur, f, t, basis);
  const uniRanked = uniShare
    ? Object.entries(uniShare)
        .sort(([, a], [, b]) => b - a)
        .map(([brand]) => brand)
    : null;
  const rank = uniRanked
    ? uniRanked.findIndex((b) => b === cur.own) + 1
    : now.brands.findIndex((b) => b.name === cur.own) + 1;
  const rankPrev = prv
    ? prv.brands.findIndex((b) => b.name === cur.own) + 1
    : 0;
  const curDays = daysOf(cur);
  const prevDays = daysOf(prev);
  const cmp = mePrev
    ? `vs ${periodLabel(win.previous_start, win.previous_end)}${prevDays < curDays ? " · partial" : ""}`
    : "no comparison data";

  const chrome = (
    <>
      <ReconBanner data={cur} basis={basis} />
      <BasisBanner
        data={cur}
        basis={basis}
        prevDays={prevDays}
        curDays={curDays}
      />
    </>
  );

  // The whole strip collapses to one tile when the window holds no crawl days
  // for the brand: seven em-dashes under seven labels is not more informative.
  if (!me) {
    return (
      <>
        {chrome}
        <Kpis>
          <Kpi
            label="GMV"
            value="—"
            foot={`No crawl days in ${periodLabel(f, t)}.`}
          />
        </Kpis>
      </>
    );
  }

  const spark = (
    label: string,
    values: Array<number | null>,
    kind: TrendSpec["kind"],
    tone: "up" | "down" | "flat",
    subtitle: string,
    formula: string,
  ): ReactNode => (
    <Spark
      spec={{ label, dates: ser.dates, values, kind, subtitle, formula }}
      tone={tone}
      w={150}
      h={26}
      onOpen={drill.openTrend}
    />
  );

  const items = findings({
    own: cur.own,
    networkStores: cur.network_stores,
    now,
    prv,
    skuNow: skuPool(cur, f, t, basis),
    skuPrv: prev
      ? skuPool(prev, prev.window.start, prev.window.end, basis)
      : [],
    curLabel: periodLabel(f, t),
    prevLabel: periodLabel(win.previous_start, win.previous_end),
  });

  return (
    <>
      {chrome}
      <Kpis>
        <Kpi
          label={`GMV (${basisL})`}
          value={fmt(me.gmv, "money")}
          // The definition, stated per basis rather than as one sentence that is
          // only true on one of them: MRP is the portal's own rupee figure, SP
          // has to be reconstructed because the portal publishes no realised price.
          title={
            basis === "mrp"
              ? "SUM(sales_value_actual) from the portal where it exists, else SUM(portal units) x SUM(final_est_sales_mrp)/SUM(final_est_units) — pooled across every geography"
              : "SUM(portal units) x SUM(final_est_sales_sp)/SUM(final_est_units), pooled nationally — each geography row uses its own city price, so the rows sum close to but not exactly onto this"
          }
          {...(mePrev
            ? { delta: <Delta cur={me.gmv} prev={mePrev.gmv} good="up" /> }
            : {})}
          spark={spark(
            `GMV (${basisL})`,
            ser.gmv,
            "money",
            toneOf(me.gmv, mePrev?.gmv ?? null, "up"),
            `Measured sales per day on the ${basisL} basis.`,
            `portal units x crawl ${basisL} price per unit, per day.`,
          )}
          foot={`measured · portal, all listings · ${cmp}`}
        />
        <Kpi
          label="Units"
          value={fmt(me.units, "int")}
          title={`SUM(actual_units_sold) — portal, ${cur.own} only`}
          {...(mePrev
            ? { delta: <Delta cur={me.units} prev={mePrev.units} good="up" /> }
            : {})}
          spark={spark(
            "Units",
            ser.units,
            "int",
            toneOf(me.units, mePrev?.units ?? null, "up"),
            "Portal units per day.",
            "SUM(actual_units_sold) per day.",
          )}
          foot={`portal actuals · ${cmp}`}
        />
        <Kpi
          label="Market share"
          value={fmt(me.share, "pct2")}
          title={`${cur.own} measured ${basisL} value / (${cur.own} + every other ${cur.universe_name ?? cur.category} brand, crawl-estimated) — same basis on both sides`}
          {...(mePrev
            ? {
                delta: <Pp cur={me.share} prev={mePrev.share} />,
              }
            : {})}
          spark={spark(
            "Market share",
            ser.share,
            "pct2",
            mePrev ? ppTone(me.share, mePrev.share) : seriesTone(ser.share),
            `${cur.own}'s share of the ${cur.category} market, per day.`,
            `${cur.own} measured value / (${cur.own} + rivals' crawl-estimated value) per day, ${basisL} basis.`,
          )}
          foot={`of ${fmt(now.size, "money")} market (${basisL}) · rivals estimated`}
        />
        <Kpi
          label="Rank in category"
          value={rank ? `#${rank}` : "—"}
          title={`Position by ${basisL} value among the named brands`}
          // A smaller number is better, so the tone is inverted against every
          // other tile — and no sparkline, because a rank is a position, not a
          // series: a line through #1 #1 #2 #1 invites reading noise as trend.
          {...(rankPrev && rank
            ? {
                delta: (
                  <div
                    className={`delta ${rank < rankPrev ? "up" : rank > rankPrev ? "down" : "flat"}`}
                  >
                    {rank === rankPrev ? "unchanged" : `was #${rankPrev}`}
                  </div>
                ),
              }
            : {})}
          foot={`of ${uniRanked ? uniRanked.length : now.brands.length} named brands · a position, not a series`}
        />
        <Kpi
          label="OSA"
          value={<Banded v={me.osa} kind="pct2" />}
          title="SUM(osa_numerator) / SUM(city_live_stores), pooled across the selected period — single SKUs only"
          {...(mePrev ? { delta: <Pp cur={me.osa} prev={mePrev.osa} /> } : {})}
          spark={spark(
            "OSA %",
            ser.osa,
            "pct",
            mePrev ? ppTone(me.osa, mePrev.osa) : seriesTone(ser.osa),
            "Availability per day.",
            "SUM(osa_numerator) / SUM(city_live_stores) per day — pooled, never an average.",
          )}
          foot="pooled store-days · virtual bundles excluded"
        />
        <Kpi
          label="DS Listing %"
          value={<Banded v={me.listing} kind="pct2" />}
          title="SUM(stores_present) / SUM(city_live_stores) — share of dark stores carrying the brand"
          {...(mePrev
            ? { delta: <Pp cur={me.listing} prev={mePrev.listing} /> }
            : {})}
          spark={spark(
            "DS Listing %",
            ser.listing,
            "pct",
            mePrev
              ? ppTone(me.listing, mePrev.listing)
              : seriesTone(ser.listing),
            `Distribution across dark stores, per day.`,
            "SUM(stores_present) / SUM(city_live_stores) per day — pooled, never an average.",
          )}
          foot={`${fmt(me.stores, "int")} of ~${fmt(cur.network_stores, "int")} stores on Blinkit`}
        />
        {/* ASP card removed per O'cean feedback (86d4c668m item 2) */}
        {(() => {
          // Ad sales from the marketing overview — campaign-level sum, not SKU-level.
          const periods: Array<Record<string, unknown>> = Array.isArray(
            mkt.data?.periods,
          )
            ? mkt.data.periods
            : Array.isArray(mkt.data?.rows)
              ? mkt.data.rows
              : [];
          const mCur = periods.find((r) => r["period"] === "current");
          const mPrv = periods.find((r) => r["period"] === "previous");
          const adSales = mCur ? Number(mCur["ad_sales"] ?? null) : null;
          const adSalesPrev = mPrv ? Number(mPrv["ad_sales"] ?? null) : null;
          const campaigns = mCur ? Number(mCur["campaigns"] ?? 0) : null;
          const rawSeries: Array<Record<string, unknown>> = Array.isArray(
            mkt.data?.series,
          )
            ? mkt.data.series
            : [];
          const adDays = rawSeries.map((d) => ({
            day: typeof d["day"] === "string" ? d["day"] : "",
            v: Number(d["ad_sales"] ?? null),
          }));
          return (
            <Kpi
              label="Ad Sales"
              value={fmt(Number.isFinite(adSales) ? adSales : null, "money")}
              title="SUM(direct_sales + indirect_sales) across all campaigns — campaign-level, not SKU-level"
              {...(Number.isFinite(adSalesPrev)
                ? {
                    delta: <Delta cur={adSales} prev={adSalesPrev} good="up" />,
                  }
                : {})}
              spark={spark(
                "Ad Sales",
                adDays.map((d) => (Number.isFinite(d.v) ? d.v : null)),
                "money",
                Number.isFinite(adSalesPrev)
                  ? toneOf(adSales, adSalesPrev, "up")
                  : "flat",
                "Total ad-attributed sales per day.",
                "SUM(direct_sales + indirect_sales) per day — campaign level.",
              )}
              foot={
                campaigns
                  ? `${fmt(campaigns, "int")} campaigns`
                  : mkt.isLoading
                    ? "loading…"
                    : "no ad data"
              }
            />
          );
        })()}
      </Kpis>

      <Findings
        title="What this means"
        items={items.map((x) => ({
          key: x.key,
          text: <Rich text={x.text} />,
          meta: x.meta,
        }))}
        disclaimer="Computed live from this page's own data each time the period changes — not a maintained list. Each finding shows the figures behind it, so any claim here can be checked against the MCP."
      />

      <GrowthCard
        own={cur.own}
        basis={basis}
        now={now}
        prv={prv}
        me={me}
        mePrev={mePrev}
        scope={`${periodLabel(f, t)} vs ${periodLabel(win.previous_start, win.previous_end)}`}
        horizon={win.as_of}
      />

      {drill.top?.kind === "trend" ? (
        <TrendModal
          spec={drill.top.spec}
          horizon={win.as_of}
          onClose={drill.pop}
        />
      ) : null}
    </>
  );
}

/**
 * Growth vs the category — three diverging bars on one shared scale.
 *
 * One scale across all three is the entire argument of the card: drawn
 * separately, a brand growing 4% and a market growing 1% auto-normalise and
 * look equally fast. "Every other brand" is the market minus the own brand,
 * which is what isolates whether the movement was ours or the market's.
 */
function GrowthCard({
  own,
  basis,
  now,
  prv,
  me,
  mePrev,
  scope,
  horizon,
}: {
  own: string;
  basis: Basis;
  now: { size: number };
  prv: { size: number } | null;
  me: BrandRow;
  mePrev: BrandRow | null;
  scope: string;
  horizon: string;
}) {
  if (!prv || !mePrev) {
    return (
      <Card title="Growth vs the category">
        <div className="empty">No comparable period in the loaded window.</div>
      </Card>
    );
  }
  const mine = me.gmv ?? 0;
  const minePrev = mePrev.gmv ?? 0;
  if (!minePrev || !prv.size) {
    return (
      <Card title="Growth vs the category">
        <div className="empty">
          The comparison period holds no sales to grow from.
        </div>
      </Card>
    );
  }
  const bG = ((mine - minePrev) / minePrev) * 100;
  const cG = ((now.size - prv.size) / prv.size) * 100;
  const rest = now.size - mine;
  const restPrev = prv.size - minePrev;
  const rG = restPrev ? ((rest - restPrev) / restPrev) * 100 : 0;
  const mx = Math.max(Math.abs(bG), Math.abs(cG), Math.abs(rG), 1);

  const bar = (label: string, v: number): ReactNode => (
    <div className="stat-row" key={label}>
      <span className="stat-swatch severity-low" />
      <div className="stat-body">
        <div className="stat-label">{label}</div>
        <div
          style={{
            height: 8,
            borderRadius: 99,
            background: "var(--line)",
            marginTop: 6,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              bottom: 0,
              width: 1,
              background: "var(--ink-3)",
              opacity: 0.4,
            }}
          />
          <div
            style={{
              position: "absolute",
              ...(v >= 0 ? { left: "50%" } : { right: "50%" }),
              width: `${Math.min(50, (Math.abs(v) / mx) * 50)}%`,
              height: "100%",
              background: v >= 0 ? "var(--up)" : "var(--down)",
            }}
          />
        </div>
      </div>
      <div
        className="stat-val"
        style={{ color: v >= 0 ? "var(--up)" : "var(--down)" }}
      >
        {v > 0 ? "+" : ""}
        {v.toFixed(1)}%
      </div>
    </div>
  );

  return (
    <Card
      title="Growth vs the category"
      scope={scope}
      asof={`Data to ${horizon}`}
      foot={
        <>
          {own} is <b>measured</b> — portal units x crawl price — while the
          market total and every other brand are <b>crawl estimates</b>, all on
          the {basisLabel(basis)} basis. &quot;Every other brand&quot; is the
          market minus {own}; it isolates whether the movement was ours or the
          market&apos;s. Growth rates are ratios of summed atoms for each
          period, never averages.
        </>
      }
    >
      <div className="panel-body">
        <div
          className="finding-text"
          style={{ fontSize: 14, marginBottom: 14 }}
        >
          {bG > cG ? (
            <>
              {own} grew{" "}
              <b>{(bG - cG).toFixed(1)} points faster than the category</b> —
              share was won.
            </>
          ) : (
            <>
              The category grew{" "}
              <b>
                {(cG - bG).toFixed(1)} points faster than {own}
              </b>{" "}
              — share was lost even though sales {bG > 0 ? "rose" : "fell"}.
            </>
          )}
        </div>
        <div className="stat-list">
          {bar(own, bG)}
          {bar("Category total", cG)}
          {bar("Every other brand", rG)}
        </div>
      </div>
    </Card>
  );
}

/* ══ 2. Competition ══════════════════════════════════════════════════════ */

export function CompetitionPage({
  customer,
  category,
  range,
  basis,
}: PageProps) {
  const { loading, error, cur, prev, win } = useCeoAtoms(
    customer,
    range,
    category,
  );
  // Use the category resolved by the atoms endpoint (which includes universe
  // lookup). Falls back to the atoms' own category, then the prop.
  const resolvedCategory = cur?.category ?? category;
  const sovQ = useReport("/v1/reports/ceo/brand-sov", customer, range, {
    category: resolvedCategory ?? category ?? "",
  });
  const drill = useDrill();
  const gate = Gate({ customer, loading, error, data: cur });
  if (gate) return gate;
  if (!cur || !win) return null;

  const [f, t] = span(cur);
  const raw = marketPool(cur, f, t, basis);
  const rawPrv = prev
    ? marketPool(prev, prev.window.start, prev.window.end, basis)
    : null;

  // When the API returns universe_market_share (from tf_market_share_v3_universe),
  // override share values and re-sort to match MCP ranking exactly —
  // same fix as MarketPositionPage.
  const applyUniverse = (
    pool: { brands: BrandRow[]; other: BrandRow | null; size: number },
    uni: Record<string, number> | undefined,
  ) => {
    if (!uni) return pool;
    const brands = pool.brands.map((b) => ({
      ...b,
      share: uni[b.name] ?? b.share,
    }));
    brands.sort((a, b) => (b.share ?? 0) - (a.share ?? 0));
    return { ...pool, brands };
  };
  const now = applyUniverse(raw, cur.universe_market_share);
  const prv = rawPrv
    ? applyUniverse(rawPrv, prev?.universe_market_share)
    : null;

  const geoNow = geoPool(cur, f, t, basis);
  const geoPrv = prev
    ? geoPool(prev, prev.window.start, prev.window.end, basis)
    : [];
  const nat = national(cur.geo_atoms, cur.own, f, t, basis);
  const curDays = daysOf(cur);
  const prevDays = daysOf(prev);
  const cmpLabel = prv
    ? periodLabel(win.previous_start, win.previous_end)
    : null;
  // Narrowed once, outside the JSX: `top` is a union and the callbacks below
  // lose the narrowing the moment they close over it.
  const geoOpen = drill.top?.kind === "geo" ? drill.top.name : null;
  const brandOpen = drill.top?.kind === "brand" ? drill.top.name : null;
  const trendOpen = drill.top?.kind === "trend" ? drill.top.spec : null;

  return (
    <>
      <ReconBanner data={cur} basis={basis} />
      <BasisBanner
        data={cur}
        basis={basis}
        prevDays={prevDays}
        curDays={curDays}
      />
      <CompetitorCard
        data={cur}
        basis={basis}
        now={now}
        prv={prv}
        scope={periodLabel(f, t)}
        horizon={win.as_of}
        cmpLabel={cmpLabel}
        onBrand={(name) => {
          drill.push({ kind: "brand", name });
        }}
        sovRows={Array.isArray(sovQ.data?.rows) ? sovQ.data.rows : undefined}
      />
      <GeographyCard
        data={cur}
        basis={basis}
        rows={geoNow}
        prev={geoPrv}
        scope={periodLabel(f, t)}
        cmpLabel={cmpLabel}
        nationalAsp={nat?.asp ?? null}
        onGeo={(name) => {
          drill.push({ kind: "geo", name });
        }}
        onTrend={drill.openTrend}
      />
      {geoOpen !== null ? (
        <GeoDrill
          data={cur}
          basis={basis}
          row={geoNow.find((r) => r.name === geoOpen) ?? null}
          prevRow={geoPrv.find((r) => r.name === geoOpen) ?? null}
          scope={periodLabel(f, t)}
          cmpLabel={cmpLabel}
          horizon={win.as_of}
          onTrend={drill.openTrend}
          onClose={drill.pop}
        />
      ) : null}
      {brandOpen !== null ? (
        <BrandSkuDrill
          customer={customer}
          brand={brandOpen}
          isOwn={brandOpen === cur.own}
          category={cur.category}
          categoryLabel={cur.universe_name ?? cur.category}
          range={range}
          basis={basis}
          scope={periodLabel(f, t)}
          horizon={win.as_of}
          onClose={drill.pop}
        />
      ) : null}
      {trendOpen ? (
        <TrendModal spec={trendOpen} horizon={win.as_of} onClose={drill.pop} />
      ) : null}
    </>
  );
}

interface BrandSov {
  overall: number | null;
  ad: number | null;
  organic: number | null;
}

function CompetitorCard({
  data,
  basis,
  now,
  prv,
  scope,
  horizon,
  cmpLabel,
  onBrand,
  sovRows,
}: {
  data: CeoAtoms;
  basis: Basis;
  now: { brands: BrandRow[]; other: BrandRow | null; size: number };
  prv: { brands: BrandRow[]; other: BrandRow | null } | null;
  scope: string;
  horizon: string;
  cmpLabel: string | null;
  onBrand?: (name: string) => void;
  sovRows?: Array<Record<string, unknown>>;
}) {
  const basisL = basisLabel(basis);
  const prevOf = (name: string): BrandRow | null => {
    if (!prv) return null;
    return (
      (name === data.other
        ? prv.other
        : prv.brands.find((b) => b.name === name)) ?? null
    );
  };

  // Brand-level SOV from the dedicated endpoint, keyed by brand name.
  const sovMap = new Map<string, BrandSov>();
  const sovPrevMap = new Map<string, BrandSov>();
  for (const r of sovRows ?? []) {
    const brand = typeof r["brand"] === "string" ? r["brand"] : "";
    const entry: BrandSov = {
      overall: typeof r["overall_sov"] === "number" ? r["overall_sov"] : null,
      ad: typeof r["ad_sov"] === "number" ? r["ad_sov"] : null,
      organic: typeof r["organic_sov"] === "number" ? r["organic_sov"] : null,
    };
    if (r["period"] === "current") sovMap.set(brand, entry);
    else if (r["period"] === "previous") sovPrevMap.set(brand, entry);
  }

  const cols: Col[] = [
    { label: "#" },
    { label: "Brand" },
    {
      label: "Market share",
      type: "num",
      title: `Brand ${basisL} value / market ${basisL} value. ${data.own} measured, rivals crawl-estimated`,
    },
    {
      label: "OSA %",
      type: "num",
      title: "SUM(osa_numerator) / SUM(city_live_stores), pooled",
    },
    {
      label: "Organic SOV",
      type: "num",
      title:
        "Organic share of voice in this category — Hector-derived from SERP crawls",
    },
    {
      label: "Ad SOV",
      type: "num",
      title:
        "Sponsored share of voice in this category — Hector-derived from SERP crawls",
    },
    {
      label: "Overall SOV",
      type: "num",
      title: "Total share of voice (organic + ad weighted) in this category",
    },
    {
      label: "DS listing %",
      type: "num",
      title: "SUM(stores_present) / SUM(city_live_stores)",
    },
    {
      label: "Discount %",
      type: "num",
      title: "1 − SUM(final_est_sales_sp) / SUM(final_est_sales_mrp)",
    },
  ];

  const rowOf = (
    b: BrandRow,
    i: number,
  ): {
    key: string;
    cls?: string;
    cells: ReactNode[];
  } => {
    const own = b.name === data.own;
    const other = b.name === data.other;
    const p = prevOf(b.name);
    const clickable = !other && onBrand;
    return {
      key: b.name,
      ...(own
        ? { cls: clickable ? "is-own clickable" : "is-own" }
        : other
          ? { cls: "is-total" }
          : clickable
            ? { cls: "clickable" }
            : {}),
      ...(clickable
        ? {
            onClick: () => {
              onBrand(b.name);
            },
          }
        : {}),
      cells: [
        <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
          {other ? "" : String(i + 1).padStart(2, "0")}
        </span>,
        <RowLabel
          name={
            other ? (
              "All other brands"
            ) : (
              <>
                {b.name}
                {own ? <span className="tag linked">you</span> : null}
              </>
            )
          }
          {...(other
            ? {
                sub: `every other ${data.universe_name ?? data.category} brand, pooled`,
              }
            : {})}
        />,
        <MetricCell
          value={fmt(b.share, "pct2")}
          {...(p ? { delta: <Pp cur={b.share} prev={p.share} /> } : {})}
        />,
        <MetricCell
          value={<Banded v={b.osa} kind="pct" />}
          {...(p ? { delta: <Pp cur={b.osa} prev={p.osa} /> } : {})}
        />,
        ...(() => {
          const s = other ? null : (sovMap.get(b.name) ?? null);
          const sp = other ? null : (sovPrevMap.get(b.name) ?? null);
          return [
            <MetricCell
              key="osov"
              value={fmt(s?.organic ?? null, "pct")}
              {...(sp
                ? { delta: <Pp cur={s?.organic ?? null} prev={sp.organic} /> }
                : {})}
            />,
            <MetricCell
              key="asov"
              value={fmt(s?.ad ?? null, "pct")}
              {...(sp
                ? { delta: <Pp cur={s?.ad ?? null} prev={sp.ad} /> }
                : {})}
            />,
            <MetricCell
              key="tsov"
              value={fmt(s?.overall ?? null, "pct")}
              {...(sp
                ? { delta: <Pp cur={s?.overall ?? null} prev={sp.overall} /> }
                : {})}
            />,
          ];
        })(),
        <MetricCell
          value={fmt(b.listing, "pct")}
          {...(p ? { delta: <Pp cur={b.listing} prev={p.listing} /> } : {})}
        />,
        <MetricCell
          value={fmt(b.disc, "pct")}
          {...(p ? { delta: <Pp cur={b.disc} prev={p.disc} /> } : {})}
        />,
      ],
    };
  };

  const rows = now.brands.map((b, i) => rowOf(b, i));
  if (now.other) rows.push(rowOf(now.other, -1));

  return (
    <TableCard
      title="Competitive position"
      scope={scope}
      asof={`Data to ${horizon} · ${basisL} basis`}
      cols={cols}
      rows={rows}
      foot={
        <>
          <b>{data.own} is measured, rivals are estimated.</b> {data.own}&apos;s
          value is portal units x crawl price (the MCP&apos;s definition).
          Rivals have no portal data, so their value, units and ASP are crawl
          estimates — internally consistent, but measured against {data.own} the
          crawl undercounts units ~11.5% and runs ASP ~16% high, so rival ASPs
          likely read high too. That bias is stated, not corrected.
          <br />
          <b>Market share</b> = brand value ÷ ({data.own} + every other{" "}
          {data.category} brand) on the <b>{basisL}</b> basis — switch the basis
          and both sides move together. Single SKUs only for every brand;
          virtual bundles are excluded until the MCP&apos;s bundle table ships.
          <br />
          <b>ASP is a selling price</b> and ignores the basis toggle; the
          MRP-per-unit for {data.own} sits under the ASP card.{" "}
          <b>No competitor rupee sales appear here, by design</b> — share,
          availability, listing and price are public shelf signals; a
          rival&apos;s revenue is not ours to publish.
          <br />
          Deltas are against {cmpLabel ?? "the comparison period"}; <b>pp</b>{" "}
          means percentage points.
          <br />
          {/* THE DOCUMENTED DEFECT, CARRIED. The published report's footnote claims
              an exclusion its own denominator does not make: activeBrands() tests
              the exclusion list against the keys of national_atoms, and the
              excluded brand is not a key — its volume sits inside the pooled tail.
              Stating that is the honest version; silently dropping the brand here
              would restate a client-facing market share without saying so. */}
          <b>No brand is excluded from this denominator.</b> The market total is
          every brand the {data.universe_name ?? data.category} crawl carries,
          including any the published report&apos;s footnote names as excluded —
          that filter tests the exclusion list against the named-brand list, and
          the excluded brand is not on it, so it removes nothing there either.
          Share here is therefore measured against the full category, and the
          published copy&apos;s figure can also differ from this one because its
          stored rival atoms are refreshed nightly rather than per request.
          <br />
          Store counts are mean live stores carrying the brand on a{" "}
          <code>COUNT(DISTINCT store_id)</code> basis, against Blinkit&apos;s
          total network of about {fmt(data.network_stores, "int")}.
        </>
      }
    />
  );
}

function GeographyCard({
  data,
  basis,
  rows,
  prev,
  scope,
  cmpLabel,
  nationalAsp,
  onGeo,
  onTrend,
}: {
  data: CeoAtoms;
  basis: Basis;
  rows: GeoRow[];
  prev: GeoRow[];
  scope: string;
  cmpLabel: string | null;
  nationalAsp: number | null;
  onGeo: (name: string) => void;
  onTrend: (spec: TrendSpec) => void;
}) {
  const basisL = basisLabel(basis);
  if (!rows.length) {
    return (
      <Card title="By geography">
        <div className="empty">No crawl days in {scope}.</div>
      </Card>
    );
  }
  const pOf = (n: string): GeoRow | undefined => prev.find((r) => r.name === n);
  const totOwn = rows.reduce((a, r) => a + (r.own?.gmv ?? 0), 0);

  const cols: Col[] = [
    { label: "Geography" },
    {
      label: `${data.own} GMV`,
      type: "num",
      title: `Measured: portal units x crawl ${basisL} price, inside this geography`,
    },
    {
      label: "Market share",
      type: "num",
      title: `${data.own} measured value / (${data.own} + rivals' crawl value) INSIDE this geography`,
    },
    { label: `vs ${cmpLabel ?? "—"}`, type: "num" },
    {
      label: "Rank",
      type: "center",
      title: "Position among the named brands here",
    },
    {
      label: "Leader",
      type: "num",
      title: "Largest brand here, and the share gap to it",
    },
    {
      label: "OSA %",
      type: "num",
      title: "SUM(osa_numerator) / SUM(city_live_stores), pooled",
    },
    {
      label: "ASP",
      type: "num",
      title:
        "SUM(final_est_sales_sp) / SUM(final_est_units) here — a selling price, unaffected by the basis toggle",
    },
    { label: `Share of ${data.own} sales`, type: "num" },
    { label: "Daily share", type: "center" },
  ];

  return (
    <TableCard
      title="By geography"
      scope={scope}
      asof={`${rows.length} geographies · ${basisL} basis · click a row for the brand matrix`}
      cols={cols}
      rows={rows.map((r) => {
        const p = pOf(r.name);
        const residual = r.name === data.residual;
        return {
          key: r.name,
          cls: `clickable${residual ? " is-total" : ""}`,
          onClick: () => {
            onGeo(r.name);
          },
          cells: [
            <RowLabel
              name={r.name}
              sub={
                residual
                  ? "every other city, pooled"
                  : `${r.brandCount} named brands`
              }
            />,
            <MetricCell
              value={fmt(r.own?.gmv ?? null, "money")}
              {...(p?.own && r.own
                ? {
                    delta: <Delta cur={r.own.gmv} prev={p.own.gmv} good="up" />,
                  }
                : {})}
            />,
            // No delta inside this cell: the comparison gets its own column,
            // because a share and a share move are read against each other.
            <MetricCell value={fmt(r.share, "pct2")} />,
            p ? (
              <Pp cur={r.share} prev={p.share} />
            ) : (
              <div className="delta flat">no comparison</div>
            ),
            // #1 green, #2 amber, #3+ red — a threshold, not a direction, so it
            // is coloured text rather than a delta.
            <span
              className={`verdict ${r.rank === 1 ? "scale" : r.rank <= 2 ? "optimise" : "stop"}`}
            >
              #{r.rank}
            </span>,
            r.leader === data.own ? (
              <span style={{ color: "var(--up)", fontWeight: 600 }}>leads</span>
            ) : (
              <div className="metric-figs">
                <div className="metric-val">{r.leader ?? "—"}</div>
                <div className="delta down">
                  {r.gap === null
                    ? ""
                    : `−${(Math.round(r.gap * 10) / 10).toFixed(1)} pp`}
                </div>
              </div>
            ),
            <Banded v={r.osa} kind="pct" bold />,
            <MetricCell
              value={fmt(r.asp, "price")}
              {...(p
                ? { delta: <Delta cur={r.asp} prev={p.asp} good="neutral" /> }
                : {})}
            />,
            totOwn && r.own
              ? `${(((r.own.gmv ?? 0) / totOwn) * 100).toFixed(1)}%`
              : "—",
            <Spark
              spec={{
                label: `${r.name} — ${data.own} share`,
                dates: r.dates,
                values: r.shareSeries,
                kind: "pct2",
                subtitle: `${data.own}'s share of this geography, per day.`,
                formula: `${data.own} measured value / market value inside this geography, per day, ${basisL} basis.`,
              }}
              tone={p ? ppTone(r.share, p.share) : seriesTone(r.shareSeries)}
              onOpen={onTrend}
            />,
          ],
        };
      })}
      foot={
        <>
          Where share is won and lost —{" "}
          <b>follows the date picker and the basis toggle</b>, pooled from brand
          × geography × day atoms. Geographies that pool several cities (Delhi
          NCR is one) sum numerators and denominators across their members and
          never average them; the member list itself is not served by this
          endpoint, so no city count is shown against the name.
          <br />
          <b>{data.residual}</b> pools every city outside the named ones so the
          rows sum to the pan-India figure; it is pinned last because a bucket
          of 200-odd cities is not a league position.
          <br />
          Share is <b>within each geography</b>, so the brands in any row total
          100% — it is not a slice of the national figure. {data.own} measured
          from its own portal (all listings), rivals crawl-estimated.{" "}
          <b>ASP is {data.own}&apos;s per-geography selling price</b> from the
          same atoms that give the national {fmt(nationalAsp, "price")}. Click a
          geography for the full brand matrix.
        </>
      }
    />
  );
}

/** Brand matrix for one geography — every metric the overview carries. */
/** Brand SKU drill — reads /v1/availability/competition/skus + /sku-sov. */
function BrandSkuDrill({
  customer,
  brand,
  isOwn,
  category,
  categoryLabel,
  range,
  basis,
  scope,
  horizon,
  onClose,
}: {
  customer: string;
  brand: string;
  /** True when this is the dashboard owner's brand; false for competitors. */
  isOwn: boolean;
  /** L2 category for API queries. */
  category: string;
  /** Display label — universe name when available, otherwise same as category. */
  categoryLabel: string;
  range: DateRange;
  basis: Basis;
  scope: string;
  horizon: string;
  onClose: () => void;
}) {
  const q = useReport("/v1/availability/competition/skus", customer, range, {
    category,
    limit: 200,
  });
  const sovQ = useReport(
    "/v1/availability/competition/sku-sov",
    customer,
    range,
    { category },
  );
  const rows: Array<Record<string, unknown>> = q.data?.rows ?? [];
  const sovRows: Array<Record<string, unknown>> = sovQ.data?.rows ?? [];

  // Build SOV maps keyed by product_id for current and previous periods.
  type SovEntry = {
    organic: number | null;
    ad: number | null;
    overall: number | null;
  };
  const sovCur = new Map<string, SovEntry>();
  const sovPrev = new Map<string, SovEntry>();
  for (const r of sovRows) {
    const pid = typeof r["product_id"] === "string" ? r["product_id"] : "";
    const entry: SovEntry = {
      organic: typeof r["organic_sov"] === "number" ? r["organic_sov"] : null,
      ad: typeof r["ad_sov"] === "number" ? r["ad_sov"] : null,
      overall: typeof r["overall_sov"] === "number" ? r["overall_sov"] : null,
    };
    if (r["period"] === "current") sovCur.set(pid, entry);
    else if (r["period"] === "previous") sovPrev.set(pid, entry);
  }

  const skus = rows
    .filter((r) => typeof r["brand"] === "string" && r["brand"] === brand)
    .map((r) => ({
      pid: typeof r["product_id"] === "string" ? r["product_id"] : "",
      name: typeof r["sku_name"] === "string" ? r["sku_name"] : "—",
      sales: Number(r["sales"] ?? 0),
      osa: Number(r["wt_osa"] ?? null),
      sp: Number(r["sp"] ?? null),
      mrp: Number(r["mrp"] ?? null),
      discount: Number(r["discount_pct"] ?? null),
      share: Number(r["category_share"] ?? null),
      grammage: typeof r["grammage"] === "string" ? r["grammage"] : "",
    }));
  const brandTotal = skus.reduce((a, s) => a + s.sales, 0);

  return (
    <Modal
      title={`${brand} — SKU breakdown`}
      count={`${skus.length} SKU${skus.length === 1 ? "" : "s"} · ${scope}`}
      foot={
        q.isLoading
          ? "Loading…"
          : skus.length === 0
            ? `No SKU data for ${brand} in ${categoryLabel}.`
            : `${brand}'s SKUs in ${categoryLabel}, sorted by sales contribution. Same source as the MCP (tf_city_metrics). All figures are crawl estimates.`
      }
      onClose={onClose}
    >
      <TableCard
        title={`${brand} — SKU breakdown`}
        scope={`${skus.length} SKU${skus.length === 1 ? "" : "s"} · ${scope}`}
        asof={`Data to ${horizon}`}
        cols={[
          { label: "SKU" },
          // 14zkq6bd1nm: hide sales columns for competitor brands
          ...(isOwn
            ? [
                { label: `Sales (${basisLabel(basis)})`, type: "num" as const },
                {
                  label: "% of brand",
                  type: "num" as const,
                  title: "This SKU's share of the brand's total sales",
                },
              ]
            : []),
          { label: "Category share", type: "num" },
          { label: "OSA %", type: "num" },
          { label: "ASP", type: "num" },
          { label: "Discount %", type: "num" },
          {
            label: "Organic SOV",
            type: "num",
            title: "Organic share of voice — Hector-derived from SERP crawls",
          },
          {
            label: "Ad SOV",
            type: "num",
            title: "Sponsored share of voice — Hector-derived from SERP crawls",
          },
          {
            label: "Overall SOV",
            type: "num",
            title: "Total share of voice (organic + ad weighted)",
          },
        ]}
        rows={[
          ...skus.map((s) => {
            const sc = sovCur.get(s.pid) ?? null;
            const sp = sovPrev.get(s.pid) ?? null;
            return {
              key: s.pid,
              cells: [
                <RowLabel
                  key="n"
                  name={s.name}
                  sub={`${s.pid}${s.grammage ? ` · ${s.grammage}` : ""}`}
                />,
                // 14zkq6bd1nm: hide sales cells for competitor brands
                ...(isOwn
                  ? [
                      fmt(s.sales, "money"),
                      brandTotal > 0
                        ? `${((s.sales / brandTotal) * 100).toFixed(1)}%`
                        : "—",
                    ]
                  : []),
                fmt(s.share, "pct2"),
                <span key="o" style={{ color: bandColor(s.osa) }}>
                  {fmt(s.osa, "pct")}
                </span>,
                fmt(s.sp, "price"),
                fmt(s.discount, "pct"),
                <MetricCell
                  key="osov"
                  value={fmt(sc?.organic ?? null, "pct")}
                  {...(sp
                    ? {
                        delta: (
                          <Pp cur={sc?.organic ?? null} prev={sp.organic} />
                        ),
                      }
                    : {})}
                />,
                <MetricCell
                  key="asov"
                  value={fmt(sc?.ad ?? null, "pct")}
                  {...(sp
                    ? { delta: <Pp cur={sc?.ad ?? null} prev={sp.ad} /> }
                    : {})}
                />,
                <MetricCell
                  key="tsov"
                  value={fmt(sc?.overall ?? null, "pct")}
                  {...(sp
                    ? {
                        delta: (
                          <Pp cur={sc?.overall ?? null} prev={sp.overall} />
                        ),
                      }
                    : {})}
                />,
              ] as ReactNode[],
            };
          }),
          ...(skus.length > 0
            ? [
                {
                  key: "__total",
                  cls: "is-total",
                  cells: [
                    <b key="t">{brand}</b>,
                    // 14zkq6bd1nm: hide sales totals for competitor brands
                    ...(isOwn
                      ? [
                          <b key="s">{fmt(brandTotal, "money")}</b>,
                          <b key="p">100.0%</b>,
                        ]
                      : []),
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                  ] as ReactNode[],
                },
              ]
            : []),
        ]}
      />
    </Modal>
  );
}

function GeoDrill({
  data,
  basis,
  row,
  prevRow,
  scope,
  cmpLabel,
  horizon,
  onTrend,
  onClose,
}: {
  data: CeoAtoms;
  basis: Basis;
  row: GeoRow | null;
  prevRow: GeoRow | null;
  scope: string;
  cmpLabel: string | null;
  horizon: string;
  onTrend: (spec: TrendSpec) => void;
  onClose: () => void;
}) {
  if (!row) return null;
  const basisL = basisLabel(basis);
  // "Other" last, then by value: the pooled tail is not a competitor, so it
  // must not sit inside the ladder it is the remainder of.
  const rows = [...row.brands].sort((a, b) => {
    const ao = a.brand === data.other ? 1 : 0;
    const bo = b.brand === data.other ? 1 : 0;
    return ao - bo || (b.pooled.gmv ?? 0) - (a.pooled.gmv ?? 0);
  });
  const prevOf = (
    brand: string,
  ): { share: number | null; osa: number | null } | null => {
    const p = prevRow?.brands.find((x) => x.brand === brand);
    return p ? { share: p.share, osa: p.pooled.osa } : null;
  };
  const totN = rows.reduce((a, r) => a + r.pooled.atoms.n, 0);
  const totD = rows.reduce((a, r) => a + r.pooled.atoms.dn, 0);
  const totU = rows.reduce((a, r) => a + r.pooled.units, 0);

  return (
    <Modal
      title={`${row.name} — brand matrix`}
      count={`${rows.length} rows · ${scope} · ${basisL} basis`}
      onClose={onClose}
      foot={
        <span>
          <b>{data.own} measured, rivals estimated.</b> {data.own}&apos;s units
          are portal actuals and its value is portal units x crawl {basisL}{" "}
          price; every other row is the crawl&apos;s own estimate of units and
          value. Share is within this geography on the {basisL} basis. ASP is a
          selling price for every row.{" "}
          <b>No competitor rupee sales appear here, by design.</b> Data to{" "}
          {horizon}.
        </span>
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Brand</th>
              <th className="num">Market share</th>
              <th className="num">vs {cmpLabel ?? "—"}</th>
              <th className="num">OSA %</th>
              <th className="center">Daily OSA</th>
              <th className="num">Units</th>
              <th className="num">ASP</th>
              <th className="num">Discount %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = prevOf(r.brand);
              const own = r.brand === data.own;
              const other = r.brand === data.other;
              return (
                <tr
                  key={r.brand}
                  {...(own
                    ? { className: "is-own" }
                    : other
                      ? { className: "is-total" }
                      : {})}
                >
                  <td>
                    <RowLabel
                      name={
                        other ? (
                          "All other brands"
                        ) : (
                          <>
                            {r.brand}
                            {own ? (
                              <span className="tag linked">you</span>
                            ) : null}
                          </>
                        )
                      }
                      sub={
                        r.pooled.measured
                          ? "measured · portal units"
                          : "estimated · crawl"
                      }
                    />
                  </td>
                  <td className="num" style={{ fontWeight: own ? 600 : 400 }}>
                    {fmt(r.share, "pct2")}
                  </td>
                  <td className="num">
                    {p ? (
                      <Pp cur={r.share} prev={p.share} />
                    ) : (
                      <div className="delta flat">—</div>
                    )}
                  </td>
                  <td className="num">
                    <Banded v={r.pooled.osa} kind="pct" bold />
                  </td>
                  <td className="center">
                    <Spark
                      spec={{
                        label: `${r.brand} — OSA % in ${row.name}`,
                        dates: row.dates,
                        values: osaSeriesOf(row, r.brand),
                        kind: "pct",
                        subtitle:
                          "Availability per day for this brand in this geography.",
                        formula:
                          "SUM(osa_numerator) / SUM(city_live_stores) per day.",
                      }}
                      tone={
                        p
                          ? ppTone(r.pooled.osa, p.osa)
                          : seriesTone(osaSeriesOf(row, r.brand))
                      }
                      onOpen={onTrend}
                    />
                  </td>
                  <td className="num">{fmt(r.pooled.units, "int")}</td>
                  <td className="num">{fmt(r.pooled.asp, "price")}</td>
                  <td className="num">{fmt(r.pooled.disc, "pct")}</td>
                </tr>
              );
            })}
            {/* The market line's OSA is POOLED across the brands, not averaged:
              summing both sides of the ratio is the only version that stays
              true when one brand is in forty stores and another in two thousand. */}
            <tr className="is-total">
              <td>
                <b>Market</b>
              </td>
              <td className="num">100.00%</td>
              <td />
              <td className="num">
                <b>{fmt(totD ? (100 * totN) / totD : null, "pct")}</b>
              </td>
              <td />
              <td className="num">
                <b>{fmt(totU, "int")}</b>
              </td>
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/* ══ 3. Portfolio ════════════════════════════════════════════════════════ */

type PortfolioView = "sku" | "subline";

export function PortfolioPage({ customer, category, range, basis }: PageProps) {
  const { loading, error, cur, prev, win } = useCeoAtoms(
    customer,
    range,
    category,
  );
  const drill = useDrill();
  const [view, setView] = useState<PortfolioView>("sku");
  // Sticky across openings, exactly as the report's module-level TREND is:
  // whatever metric and window you last looked at is what the next SKU opens on.
  const [metric, setMetric] = useState<TrendMetric>("gmv");
  const [trendWin, setTrendWin] = useState<TrendWindow>(30);

  const gate = Gate({ customer, loading, error, data: cur });
  if (gate) return gate;
  if (!cur || !win) return null;

  const [f, t] = span(cur);
  const skuNow = skuPool(cur, f, t, basis);
  const skuPrv = prev
    ? skuPool(prev, prev.window.start, prev.window.end, basis)
    : [];
  const curDays = daysOf(cur);
  const prevDays = daysOf(prev);
  const cmpLabel = prev
    ? periodLabel(win.previous_start, win.previous_end)
    : null;
  // Narrowed once, outside the JSX — see the note in CompetitionPage.
  const sublineOpen = drill.top?.kind === "subline" ? drill.top.name : null;
  const skuOpen = drill.top?.kind === "sku" ? drill.top.pid : null;
  const trendOpen = drill.top?.kind === "trend" ? drill.top.spec : null;

  return (
    <>
      <ReconBanner data={cur} basis={basis} />
      <BasisBanner
        data={cur}
        basis={basis}
        prevDays={prevDays}
        curDays={curDays}
      />
      {skuNow.length ? (
        <PortfolioCard
          data={cur}
          basis={basis}
          view={view}
          onView={setView}
          skuNow={skuNow}
          skuPrv={skuPrv}
          scope={periodLabel(f, t)}
          cmpLabel={cmpLabel}
          onSubline={(name) => {
            drill.push({ kind: "subline", name });
          }}
          onSku={(pid) => {
            drill.push({ kind: "sku", pid });
          }}
          onTrend={drill.openTrend}
        />
      ) : (
        <Card title="Portfolio movements">
          <div className="empty">No crawl days in {periodLabel(f, t)}.</div>
        </Card>
      )}

      {sublineOpen !== null ? (
        <SublineDrill
          name={sublineOpen}
          basis={basis}
          rows={skuNow.filter((x) => x.subline === sublineOpen)}
          prevRows={skuPrv}
          scope={periodLabel(f, t)}
          horizon={win.as_of}
          onSku={(pid) => {
            drill.push({ kind: "sku", pid });
          }}
          onClose={drill.pop}
        />
      ) : null}

      {skuOpen !== null ? (
        <SkuTrend
          data={cur}
          basis={basis}
          pid={skuOpen}
          metric={metric}
          win={trendWin}
          onMetric={(m) => {
            setMetric(m as TrendMetric);
          }}
          onWin={setTrendWin}
          horizon={win.as_of}
          onClose={drill.pop}
        />
      ) : null}

      {trendOpen ? (
        <TrendModal spec={trendOpen} horizon={win.as_of} onClose={drill.pop} />
      ) : null}
    </>
  );
}

function PortfolioCard({
  data,
  basis,
  view,
  onView,
  skuNow,
  skuPrv,
  scope,
  cmpLabel,
  onSubline,
  onSku,
  onTrend,
}: {
  data: CeoAtoms;
  basis: Basis;
  view: PortfolioView;
  onView: (v: PortfolioView) => void;
  skuNow: SkuRow[];
  skuPrv: SkuRow[];
  scope: string;
  cmpLabel: string | null;
  onSubline: (name: string) => void;
  onSku: (pid: string) => void;
  onTrend: (spec: TrendSpec) => void;
}) {
  const basisL = basisLabel(basis);
  const bySub = view === "subline";
  const unit = bySub ? "sub-line" : "SKU";
  const sublineServed = hasSublines(data.sku_meta);

  const now: Array<SkuRow | SublineRow> = bySub ? sublinePool(skuNow) : skuNow;
  const prv: Array<SkuRow | SublineRow> = bySub ? sublinePool(skuPrv) : skuPrv;
  const tn = now.reduce((a, x) => a + (x.gmv ?? 0), 0);
  const tp = prv.reduce((a, x) => a + (x.gmv ?? 0), 0);

  /** The row's identity — the product_id for a SKU, the line's name otherwise. */
  const idOf = (x: SkuRow | SublineRow): string =>
    "productId" in x ? x.productId : x.name;
  const labelOf = (x: SkuRow | SublineRow): string =>
    "label" in x ? x.label : x.name;

  const rows = now
    .map((x) => {
      const p = prv.find((y) => idOf(y) === idOf(x)) ?? null;
      return {
        x,
        prev: p,
        chg: p ? (x.gmv ?? 0) - (p.gmv ?? 0) : null,
        mixNow: tn ? ((x.gmv ?? 0) / tn) * 100 : null,
        mixPrev: p && tp ? ((p.gmv ?? 0) / tp) * 100 : null,
      };
    })
    .sort((a, b) => (b.x.gmv ?? 0) - (a.x.gmv ?? 0));

  const cols: Col[] = [
    { label: bySub ? "Sub-line" : "SKU" },
    // NOT the headline's basis. The portal overlay reaches the national and
    // geography atoms but not the per-SKU atoms, so these rows are the crawl's
    // own estimate — stated in the column tooltip and again in the footnote,
    // because a column headed the same as the headline that is 11% under it is
    // the kind of gap a reader only finds by adding the column up.
    {
      label: `GMV (${basisL})`,
      type: "num",
      title: `Crawl-estimated units x crawl ${basisL} price — NOT the portal-measured headline basis`,
    },
    {
      label: "Units",
      type: "num",
      title:
        "SUM(actual_units_sold) from the crawl, per product_id — not the portal download",
    },
    {
      label: "Change",
      type: "num",
      title: "Rupee change against the comparison period, same basis",
    },
    {
      label: "Share of portfolio",
      type: "num",
      title: `This ${unit}'s share of ${data.own}'s own sales — does the mix shift, not just the total`,
    },
    { label: "OSA %", type: "num", title: "Pooled store-day availability" },
    { label: "ASP", type: "num", title: "Selling price per unit" },
    { label: "Trend", type: "center" },
  ];

  return (
    <TableCard
      title="Portfolio movements"
      scope={scope}
      actions={
        <div className="seg">
          {(
            [
              ["sku", "By SKU"],
              ["subline", "By sub-line"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              className={view === k ? "on" : ""}
              onClick={() => {
                onView(k);
              }}
            >
              {l}
            </button>
          ))}
        </div>
      }
      asof={`${rows.length} ${bySub ? `sub-lines · ${skuNow.length} single SKUs` : "single SKUs"} · ${basisL} basis · ${cmpLabel ? `vs ${cmpLabel}` : "no comparison available"}`}
      cols={cols}
      rows={rows.map((r) => {
        const sku = "productId" in r.x ? r.x : null;
        const sub = "skus" in r.x ? r.x : null;
        return {
          key: idOf(r.x),
          cls: "clickable",
          onClick: () => {
            if (sub) onSubline(sub.name);
            else if (sku) onSku(sku.productId);
          },
          cells: [
            <RowLabel
              name={labelOf(r.x)}
              sub={
                sub
                  ? `${sub.skus.length} SKU${sub.skus.length === 1 ? "" : "s"} · click for the range`
                  : [sku?.subline, sku?.grammage, sku?.productId]
                      .filter(Boolean)
                      .join(" · ")
              }
            />,
            fmt(r.x.gmv, "money"),
            fmt(r.x.units, "int"),
            <RupeeChange chg={r.chg} />,
            <MetricCell
              value={fmt(r.mixNow, "pct")}
              {...(r.mixPrev !== null
                ? { delta: <Pp cur={r.mixNow} prev={r.mixPrev} /> }
                : {})}
            />,
            <Banded v={r.x.osa} kind="pct" />,
            fmt(r.x.asp, "price"),
            <Spark
              spec={{
                label: `${labelOf(r.x)} — GMV (${basisL})`,
                dates: r.x.dates,
                values: r.x.gmvSeries,
                kind: "money",
                subtitle: `Measured sales per day for this ${unit}.`,
                formula: bySub
                  ? "sum of the range's SKUs, per day."
                  : `portal units x crawl ${basisL} price, per day.`,
              }}
              tone={
                r.chg === null
                  ? seriesTone(r.x.gmvSeries)
                  : r.chg > 0
                    ? "up"
                    : r.chg < 0
                      ? "down"
                      : "flat"
              }
              onOpen={onTrend}
            />,
          ],
        };
      })}
      foot={
        <>
          {bySub ? (
            <>
              {sublineServed ? (
                <>
                  {data.own}&apos;s ranges as the plan file defines them, on the
                  same measured basis as the headline.{" "}
                </>
              ) : (
                <>
                  <b>These are not the plan&apos;s sub-lines.</b> The
                  client&apos;s planning lines (Core 500ml, Zero Range, Plus
                  Range, 1L Growth) live in the nightly builder&apos;s portal
                  map and this endpoint does not serve them, so the rows below
                  are grouped by the served l2 category instead. A row headed
                  with a plan line that was really a category would be a
                  fabricated range, so the grouping says what it is until the
                  field ships.{" "}
                </>
              )}
              Rupees and units are the <b>sum of each group&apos;s SKUs</b>, so
              the two views total to the same {fmt(tn, "money")} and the toggle
              can never move the number — that total is the crawl&apos;s
              estimate rather than the portal-measured headline, for the reason
              the SKU view states. OSA, ASP and discount are pooled from the
              atoms rather than averaged across SKUs, because a ratio has no
              meaningful average. <b>Click a row for its SKUs.</b>
            </>
          ) : (
            <>
              Every single SKU, largest first. The {rows.length} rows sum to{" "}
              {fmt(tn, "money")}, and that total is <b>not</b> the pan-India
              headline: the portal download is stamped onto the national and
              geography atoms but not onto the per-SKU atoms, so these rows
              carry the crawl&apos;s estimate of units — which runs about 11.5%
              under the portal. The mix, the ranking and the rupee changes
              inside this table are all consistent with each other; only the
              absolute total is on the other basis, and the banner above states
              the gap in units.
              {sublineServed
                ? " Sub-lines follow the client’s plan file."
                : " The sub-line shown against each SKU is the served l2 category: the plan’s own lines are not served by this endpoint."}{" "}
              <b>Click a SKU for its full trend.</b>
            </>
          )}
          <br />
          <b>
            Share of portfolio is the column that matters at this level:
          </b> a {unit} can grow in rupees while shrinking as a proportion of
          the business. Pack-of-N variants are virtual bundles and out of scope
          everywhere on this page.
          <br />
          <b>Competition stays on its own tab.</b> This view is {data.own}
          &apos;s own mix; the brand-level market position — share, OSA,
          listing, price against every rival — sits under Competition, because
          mixing a rival&apos;s shelf signals into a portfolio table would put
          measured and estimated figures in the same column.
        </>
      }
    />
  );
}

/**
 * The SKUs inside one range — same columns, same basis, so the rows visibly add
 * up to the row behind them. The total line's ratios are re-derived from the
 * SUMMED atoms rather than averaged across the rows above it.
 */
function SublineDrill({
  name,
  basis,
  rows: input,
  prevRows,
  scope,
  horizon,
  onSku,
  onClose,
}: {
  name: string;
  basis: Basis;
  rows: SkuRow[];
  prevRows: SkuRow[];
  scope: string;
  horizon: string;
  onSku: (pid: string) => void;
  onClose: () => void;
}) {
  const basisL = basisLabel(basis);
  const rows = [...input].sort((a, b) => (b.gmv ?? 0) - (a.gmv ?? 0));
  if (!rows.length) return null;
  const tot = rows.reduce((a, x) => a + (x.gmv ?? 0), 0);
  const totU = rows.reduce((a, x) => a + x.units, 0);
  const at = rows.reduce(
    (a, x) => ({
      n: a.n + x.atoms.n,
      dn: a.dn + x.atoms.dn,
      spe: a.spe + x.atoms.spe,
      eu: a.eu + x.atoms.eu,
      mrpe: a.mrpe + x.atoms.mrpe,
    }),
    { n: 0, dn: 0, spe: 0, eu: 0, mrpe: 0 },
  );
  const osa = at.dn ? (at.n / at.dn) * 100 : null;

  return (
    <Modal
      title={`${name} — by SKU`}
      count={`${rows.length} SKUs · ${scope} · ${basisL} basis`}
      onClose={onClose}
      foot={
        <>
          <span>
            The rows sum to the range exactly — same atoms, same derivation as
            the table behind. The total line&apos;s{" "}
            <b>OSA, ASP and discount are pooled</b>, not averaged:{" "}
            <code>SUM(osa_numerator)/SUM(city_live_stores)</code> and{" "}
            <code>SUM(final_est_sales_sp)/SUM(final_est_units)</code>.{" "}
            <b>ASP is a selling price</b> and ignores the MRP/SP toggle, which
            moves GMV only. <b>Click a SKU for its daily trend.</b>
          </span>
          <span className="spacer" />
          <span>Data to {horizon}</span>
        </>
      }
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>SKU</th>
              <th className="num">GMV ({basisL})</th>
              <th className="num">Share of range</th>
              <th className="num">Units</th>
              <th className="num">Change</th>
              <th className="num">OSA %</th>
              <th className="num">ASP</th>
              <th className="num">Discount %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p =
                prevRows.find((y) => y.productId === r.productId) ?? null;
              return (
                <tr
                  key={r.productId}
                  className="clickable"
                  onClick={() => {
                    onSku(r.productId);
                  }}
                >
                  <td>
                    <RowLabel
                      name={r.label}
                      sub={[r.grammage, r.productId]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  </td>
                  <td className="num">{fmt(r.gmv, "money")}</td>
                  <td className="num" style={{ color: "var(--ink-2)" }}>
                    {tot && r.gmv
                      ? `${((r.gmv / tot) * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                  <td className="num">{fmt(r.units, "int")}</td>
                  <td className="num">
                    <RupeeChange chg={p ? (r.gmv ?? 0) - (p.gmv ?? 0) : null} />
                  </td>
                  <td className="num">
                    <Banded v={r.osa} kind="pct" bold />
                  </td>
                  <td className="num">{fmt(r.asp, "price")}</td>
                  <td className="num">{fmt(r.disc, "pct")}</td>
                </tr>
              );
            })}
            <tr className="is-total">
              <td>
                <b>{name}</b>
              </td>
              <td className="num">
                <b>{fmt(tot, "money")}</b>
              </td>
              <td className="num">100.0%</td>
              <td className="num">
                <b>{fmt(totU, "int")}</b>
              </td>
              <td />
              <td className="num">
                <b style={{ color: bandColor(osa) ?? "var(--ink-3)" }}>
                  {fmt(osa, "pct")}
                </b>
              </td>
              <td className="num">
                <b>{fmt(at.eu ? at.spe / at.eu : null, "price")}</b>
              </td>
              <td className="num">
                <b>
                  {fmt(at.mrpe ? (1 - at.spe / at.mrpe) * 100 : null, "pct")}
                </b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/**
 * One SKU's daily trajectory, on its own window.
 *
 * Deliberately independent of the page's period: a SKU's shape is worth seeing
 * whole. In-app that window can only be as long as the atoms the page fetched,
 * so the modal caps its 7/14/30 control at what is loaded and labels it as a
 * fixed window rather than quietly drawing fewer days than the button promises.
 */
function SkuTrend({
  data,
  basis,
  pid,
  metric,
  win,
  onMetric,
  onWin,
  horizon,
  onClose,
}: {
  data: CeoAtoms;
  basis: Basis;
  pid: string;
  metric: TrendMetric;
  win: TrendWindow;
  onMetric: (m: string) => void;
  onWin: (w: TrendWindow) => void;
  horizon: string;
  onClose: () => void;
}) {
  const all: Atom[] = [...(data.sku_atoms[pid] ?? [])].sort((a, b) =>
    a.d < b.d ? -1 : 1,
  );
  if (!all.length) return null;
  const use = all.slice(-Math.min(win, all.length));
  const M = TREND_METRICS[metric];
  const values = use.map((r) => M.get(r, basis));

  // The crawl-coverage step change is found from the data, not from a named
  // date: the first day the denominator jumps by more than half. The ratio
  // stays valid across it because both sides scale together — but the reader
  // should be able to see where the shelf got bigger.
  let markerIdx = -1;
  for (let i = 1; i < use.length; i += 1) {
    const p = use[i - 1];
    const c = use[i];
    if (p && c && p.dn && c.dn / p.dn > 1.5) {
      markerIdx = i;
      break;
    }
  }
  const marker =
    markerIdx > 0
      ? {
          i: markerIdx,
          label: `crawl expands ${use[markerIdx]?.d.slice(5) ?? ""}`,
        }
      : undefined;

  const meta = data.sku_meta[pid];
  return (
    <SkuTrendModal
      title={meta?.sku_name ?? pid}
      dates={use.map((r) => r.d)}
      values={values}
      kind={M.kind}
      metric={metric}
      win={win}
      onMetric={onMetric}
      onWin={onWin}
      loadedDays={all.length}
      horizon={horizon}
      {...(marker ? { marker } : {})}
      onClose={onClose}
      foot={
        metric === "osa"
          ? "OSA is pooled per day — SUM(osa_numerator)/SUM(city_live_stores) — never an average of store percentages. A dashed marker appears on any day the crawl denominator steps up sharply; the ratio stays valid across it because both sides scale together."
          : `Daily ${M.label.toLowerCase()} from the crawl, independent of the period selected on the page — a SKU's trajectory is worth seeing whole.`
      }
    />
  );
}
